import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Shield, Eye, EyeOff, Zap, Database, FileText, MessageCircle, Users, Activity, Clock, Sparkles } from 'lucide-react';
import { toast } from 'react-toastify';
import { useAuth } from '../hooks/useAuth';

const Login = () => {
  const { login } = useAuth();
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [focusedField, setFocusedField] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.username.trim() || !formData.password.trim()) {
      toast.error('Por favor, preencha todos os campos');
      return;
    }

    setLoading(true);
    
    try {
      // Usar o hook useAuth para fazer login
      const result = await login(formData.username, formData.password);
      
      if (result.success) {
        // O hook useAuth já salva os dados e atualiza o estado
        console.log('Login realizado com sucesso:', result.data);
        console.log('🔄 Aguardando 1 segundo para atualizar a página...');
        
        // Aguardar um pouco para o toast aparecer e depois atualizar a página
        setTimeout(() => {
          console.log('🔄 Atualizando página...');
          window.location.reload();
        }, 1000);
      } else {
        console.error('Erro no login:', result.message);
      }
    } catch (error) {
      console.error('Erro no login:', error);
      toast.error('Erro de conexão. Verifique se o backend está rodando.');
    } finally {
      setLoading(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, scale: 0.9, y: 50 },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: "easeOut"
      }
    }
  };

  const floatingVariants = {
    animate: {
      y: [-10, 10, -10],
      transition: {
        duration: 6,
        repeat: Infinity,
        ease: "easeInOut"
      }
    }
  };

  return (
    <div className="min-h-screen bg-blue-50 relative overflow-hidden">
      {/* Background animado */}
      <div className="absolute inset-0">
        <motion.div
          className="absolute top-20 left-20 w-32 h-32 bg-blue-200 rounded-full opacity-30"
          variants={floatingVariants}
          animate="animate"
        />
        <motion.div
          className="absolute top-40 right-32 w-24 h-24 bg-blue-300 rounded-full opacity-30"
          variants={floatingVariants}
          animate="animate"
          style={{ animationDelay: '2s' }}
        />
        <motion.div
          className="absolute bottom-32 left-32 w-20 h-20 bg-blue-200 rounded-full opacity-30"
          variants={floatingVariants}
          animate="animate"
          style={{ animationDelay: '4s' }}
        />
      </div>

      <div className="relative z-10 flex items-center justify-center min-h-screen p-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl w-full">
          
          {/* Painel Esquerdo - Dashboard */}
          <motion.div
            variants={cardVariants}
            initial="hidden"
            animate="visible"
            className="hidden lg:block"
          >
            <div className="bg-white/95 backdrop-blur-sm rounded-3xl shadow-2xl p-8 h-full border border-white/20">
              {/* Header */}
              <motion.div 
                className="text-center mb-8"
                variants={itemVariants}
              >
                <motion.div 
                  className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-purple-500 via-purple-600 to-purple-700 rounded-full mb-6 shadow-lg"
                  whileHover={{ scale: 1.05, rotate: 5 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <Zap className="w-10 h-10 text-white" />
                </motion.div>
                <motion.h1 
                  className="text-4xl font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent mb-3"
                  variants={itemVariants}
                >
                  Painel Bitrix
                </motion.h1>
                <motion.p 
                  className="text-gray-600 text-lg"
                  variants={itemVariants}
                >
                  Sistema de Consulta e Monitoramento
                </motion.p>
              </motion.div>

              {/* Lista de APIs */}
              <motion.div 
                className="space-y-4 mb-8"
                variants={containerVariants}
                initial="hidden"
                animate="visible"
              >
                <motion.div 
                  className="flex items-center space-x-4 p-4 bg-gradient-to-r from-blue-50 to-blue-100 rounded-xl border border-blue-200/50 hover:shadow-lg transition-all duration-300 group"
                  variants={itemVariants}
                  whileHover={{ x: 5, scale: 1.02 }}
                >
                  <div className="w-4 h-4 bg-blue-500 rounded-full group-hover:animate-pulse"></div>
                  <Database className="w-6 h-6 text-blue-600" />
                  <div>
                    <div className="font-semibold text-gray-800">API Banco Bitrix</div>
                    <div className="text-sm text-gray-600">Servidor Principal</div>
                  </div>
                </motion.div>

                <motion.div 
                  className="flex items-center space-x-4 p-4 bg-gradient-to-r from-green-50 to-green-100 rounded-xl border border-green-200/50 hover:shadow-lg transition-all duration-300 group"
                  variants={itemVariants}
                  whileHover={{ x: 5, scale: 1.02 }}
                >
                  <div className="w-4 h-4 bg-green-500 rounded-full group-hover:animate-pulse"></div>
                  <FileText className="w-6 h-6 text-green-600" />
                  <div>
                    <div className="font-semibold text-gray-800">API Registro Interesse</div>
                    <div className="text-sm text-gray-600">Gerenciamento de Registros</div>
                  </div>
                </motion.div>

                <motion.div 
                  className="flex items-center space-x-4 p-4 bg-gradient-to-r from-yellow-50 to-yellow-100 rounded-xl border border-yellow-200/50 hover:shadow-lg transition-all duration-300 group"
                  variants={itemVariants}
                  whileHover={{ x: 5, scale: 1.02 }}
                >
                  <div className="w-4 h-4 bg-yellow-500 rounded-full group-hover:animate-pulse"></div>
                  <MessageCircle className="w-6 h-6 text-yellow-600" />
                  <div>
                    <div className="font-semibold text-gray-800">API Comentários</div>
                    <div className="text-sm text-gray-600">Sistema de Comentários</div>
                  </div>
                </motion.div>

                <motion.div 
                  className="flex items-center space-x-4 p-4 bg-gradient-to-r from-purple-50 to-purple-100 rounded-xl border border-purple-200/50 hover:shadow-lg transition-all duration-300 group"
                  variants={itemVariants}
                  whileHover={{ x: 5, scale: 1.02 }}
                >
                  <div className="w-4 h-4 bg-purple-500 rounded-full group-hover:animate-pulse"></div>
                  <Users className="w-6 h-6 text-purple-600" />
                  <div>
                    <div className="font-semibold text-gray-800">API Registro Conversa</div>
                    <div className="text-sm text-gray-600">Gerenciamento de Conversas</div>
                  </div>
                </motion.div>
              </motion.div>

              {/* Status */}
              <motion.div 
                className="grid grid-cols-2 gap-4"
                variants={containerVariants}
                initial="hidden"
                animate="visible"
              >
                <motion.div 
                  className="bg-gradient-to-br from-blue-100 to-blue-200 rounded-2xl p-6 text-center border border-blue-300/50 hover:shadow-lg transition-all duration-300"
                  variants={itemVariants}
                  whileHover={{ y: -5, scale: 1.05 }}
                >
                  <div className="text-3xl font-bold text-blue-700 mb-2">5</div>
                  <div className="text-sm text-blue-800 font-medium">APIs Ativas</div>
                </motion.div>
                <motion.div 
                  className="bg-gradient-to-br from-purple-100 to-purple-200 rounded-2xl p-6 text-center border border-purple-300/50 hover:shadow-lg transition-all duration-300"
                  variants={itemVariants}
                  whileHover={{ y: -5, scale: 1.05 }}
                >
                  <div className="text-3xl font-bold text-purple-700 mb-2">24/7</div>
                  <div className="text-sm text-purple-800 font-medium">Monitoramento</div>
                </motion.div>
              </motion.div>
            </div>
          </motion.div>

          {/* Painel Direito - Login */}
          <motion.div
            variants={cardVariants}
            initial="hidden"
            animate="visible"
            className="flex justify-end lg:justify-end"
          >
            <div className="bg-white/95 backdrop-blur-sm rounded-3xl shadow-2xl p-8 border border-white/20 w-full max-w-md">
              {/* Header do Login */}
              <motion.div 
                className="text-center mb-8"
                variants={containerVariants}
                initial="hidden"
                animate="visible"
              >
                <motion.div 
                  className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-purple-500 via-purple-600 to-purple-700 rounded-full mb-6 shadow-lg"
                  variants={itemVariants}
                  whileHover={{ scale: 1.05, rotate: -5 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <Shield className="w-10 h-10 text-white" />
                </motion.div>
                <motion.h2 
                  className="text-3xl font-bold text-gray-800 mb-3"
                  variants={itemVariants}
                >
                  Bem-vindo de volta
                </motion.h2>
                <motion.p 
                  className="text-gray-600 text-lg"
                  variants={itemVariants}
                >
                  Faça login para acessar o painel
                </motion.p>
              </motion.div>

              {/* Formulário de Login */}
              <motion.form 
                onSubmit={handleSubmit} 
                className="space-y-6"
                variants={containerVariants}
                initial="hidden"
                animate="visible"
              >
                {/* Campo Usuário */}
                <motion.div variants={itemVariants}>
                  <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-2">
                    Usuário
                  </label>
                  <motion.div
                    className={`relative transition-all duration-300 ${
                      focusedField === 'username' ? 'scale-105' : 'scale-100'
                    }`}
                  >
                    <input
                      type="text"
                      id="username"
                      name="username"
                      value={formData.username}
                      onChange={handleInputChange}
                      onFocus={() => setFocusedField('username')}
                      onBlur={() => setFocusedField(null)}
                      placeholder="Digite seu usuário"
                      className="w-full px-5 py-4 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-purple-200 focus:border-purple-500 transition-all duration-300 text-lg"
                      required
                    />
                    <motion.div
                      className="absolute inset-0 rounded-xl border-2 border-transparent pointer-events-none"
                      animate={{
                        borderColor: focusedField === 'username' ? '#8b5cf6' : 'transparent'
                      }}
                      transition={{ duration: 0.3 }}
                    />
                  </motion.div>
                </motion.div>

                {/* Campo Senha */}
                <motion.div variants={itemVariants}>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                    Senha
                  </label>
                  <motion.div
                    className={`relative transition-all duration-300 ${
                      focusedField === 'password' ? 'scale-105' : 'scale-100'
                    }`}
                  >
                    <input
                      type={showPassword ? 'text' : 'password'}
                      id="password"
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      onFocus={() => setFocusedField('password')}
                      onBlur={() => setFocusedField(null)}
                      placeholder="Digite sua senha"
                      className="w-full px-5 py-4 pr-14 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-purple-200 focus:border-purple-500 transition-all duration-300 text-lg"
                      required
                    />
                    <motion.div
                      className="absolute inset-0 rounded-xl border-2 border-transparent pointer-events-none"
                      animate={{
                        borderColor: focusedField === 'password' ? '#8b5cf6' : 'transparent'
                      }}
                      transition={{ duration: 0.3 }}
                    />
                    <motion.button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-0 pr-4 flex items-center"
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                    >
                      {showPassword ? (
                        <EyeOff className="h-6 w-6 text-gray-400 hover:text-gray-600 transition-colors" />
                      ) : (
                        <Eye className="h-6 w-6 text-gray-400 hover:text-gray-600 transition-colors" />
                      )}
                    </motion.button>
                  </motion.div>
                </motion.div>

                {/* Link Esqueci minha senha */}
                <motion.div variants={itemVariants} className="text-right">
                  <Link
                    to="/forgot-password"
                    className="text-sm text-purple-600 hover:text-purple-800 font-medium transition-colors duration-300"
                  >
                    Esqueci minha senha
                  </Link>
                </motion.div>

                {/* Botão de Login */}
                <motion.div variants={itemVariants}>
                  <motion.button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-gradient-to-r from-blue-500 via-purple-600 to-purple-700 text-white font-semibold py-4 px-6 rounded-xl text-lg hover:from-blue-600 hover:via-purple-700 hover:to-purple-800 focus:outline-none focus:ring-4 focus:ring-purple-300 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl"
                    whileHover={{ scale: 1.02, y: -2 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <AnimatePresence mode="wait">
                      {loading ? (
                        <motion.div
                          key="loading"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          className="flex items-center justify-center space-x-2"
                        >
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                          <span>Entrando...</span>
                        </motion.div>
                      ) : (
                        <motion.div
                          key="login"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          className="flex items-center justify-center space-x-2"
                        >
                          <Sparkles className="w-5 h-5" />
                          <span>Entrar</span>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.button>
                </motion.div>
              </motion.form>

              {/* Copyright */}
              <motion.div 
                className="mt-8 text-center"
                variants={itemVariants}
                initial="hidden"
                animate="visible"
              >
                <p className="text-sm text-gray-500">
                  © 2025 Painel Bitrix. Todos os direitos reservados.
                </p>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Login;
